import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import type {
  ProfileLinkConfig,
  ProfileLinkOptions,
} from "../../../../../client";
import {
  Button,
  LoadingSpinner,
  PopoverMenu,
  SecondarySectionTitle,
  SmartPopoverMenu,
  Switch,
} from "../../../../../components";
import { BulletSectionTitle } from "../../../../../components/section/BulletSectionTitle";
import { cn } from "../../../../../components/utils/cn";
import { useUpdateProfileLinkMotivation } from "../../../../../services/organtization/update-profile-link-motivation";
import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faUsers,
  faChevronDown,
  faChevronUp,
  faICursor,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { DumbDropdownButton } from "../../../../../components/buttons/dumb-dropdown-button/DumbDropdownButton";

import { PopoverClose } from "../../../../../components/popover";
import ProfileImageLinkSettings from "./ProfileImageLinkSettings";
import ProfileSummaryLinkSettings from "./ProfileSummaryLinkSettings";
import LanguageLinkSettings from "./LanguageLinkSettings";
import HighlightedProjectsLinkSettings from "./HighlightedProjectsLinkSettings";
import SkillStackLinkSettings from "./SkillStackLinkSettings";
import WorkExperienceLinkSettings from "./WorkExperienceLinkSettings";
import ProjectsLinkSettings from "./ProjectsLinkSettings";
import PDFSettingsLinkSettings from "./PDFSettingsLinkSettings";
import LinkSettingsLinkSettings from "./LinkSettingsLinkSettings";
import TemplateLinkSettings from "./TemplateLinkSettings";
import { useGetBackgroundJob } from "../../../../../services/jobs";
import { useQueryClient } from "@tanstack/react-query";
import { getLinkOptions } from "../../../../../services";
import { DumbDropdownButtonOption } from "../../../../../components/buttons/dumb-dropdown-button";
import { getLinkSettings } from "../../../../../services/link/get-link-settings";
import posthog from "posthog-js";
import { DefaultLeadActions } from "./DefaultLeadActions";
import { useStatsigClient } from "../../../../../hooks";

const capitalizeFirst = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

interface Props {
  disabled?: boolean;
  linkConfig?: ProfileLinkConfig;
  onUpdate: (payload: Partial<ProfileLinkConfig>) => void;
  organizationId: string;
  leadId: string;
  linkId: string;
  profileId: string;
  linkOptions: ProfileLinkOptions;
  onTranslationStarted: () => void;
  onTranslationEnded: () => void;
  isLinkLoading: boolean;
  isDefaultLead: boolean;
}

export const LinkCreatorSideMenu = ({
  disabled,
  linkConfig,
  onUpdate,
  organizationId,
  leadId,
  linkId,
  profileId,
  linkOptions,
  onTranslationStarted,
  onTranslationEnded,
  isLinkLoading,
  isDefaultLead,
}: Props) => {
  const toastId = useRef<string | number>();

  const statsigClient = useStatsigClient();
  const showTemplateEditor = statsigClient?.checkGate("cv_template_editor");

  const queryClient = useQueryClient();
  const [checkForBackgroundJob, setCheckForBackgroundJob] = useState(true);
  const [translationStarted, setTranslationStarted] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const {
    data: bgJobData,
    isSuccess,
    isError,
    error,
    fetchStatus,
  } = useGetBackgroundJob(profileId, "translation", {
    refetchInterval: checkForBackgroundJob ? 2000 : false,
  });

  useEffect(() => {
    if (!bgJobData?.isRunning) {
      setCheckForBackgroundJob(false);
      onTranslationEnded();
      queryClient.invalidateQueries(getLinkOptions(linkId));
      queryClient.invalidateQueries(getLinkSettings(linkId));
    } else {
      onTranslationStarted();
      setTranslationStarted(false);
    }
  }, [bgJobData?.isRunning]);

  useEffect(() => {
    setSelectedLanguage(linkConfig?.language || "");
  }, [linkConfig?.language]);

  const updateProfileAssignmentMotivation = useUpdateProfileLinkMotivation({
    onMutate: () => {
      toastId.current = toast.loading("Generating profile summary...");
    },
    onSuccess: (config) => {
      toast.success("Done!", { id: toastId.current });

      if (config.assignmentPitch) {
        // Include highlights if they exist in the response to prevent nullification
        const updatePayload: Partial<ProfileLinkConfig> = {
          assignmentPitch: config.assignmentPitch,
        };

        if (config.highlights) {
          updatePayload.highlights = config.highlights;
        }

        onUpdate(updatePayload);
      }
    },
    onSettled: () => {
      toast.success("Done!", { id: toastId.current });
    },
  });

  const updateAssignmentPitch = (includeName: boolean) => {
    updateProfileAssignmentMotivation.mutate({
      path: {
        organizationID: organizationId,
        leadID: leadId,
        linkID: linkId,
      },
      body: {
        includeName,
      },
    });
  };

  const englishIsReadyToUse = () => {
    return (
      linkOptions.translations &&
      linkOptions.translations.filter((t) => t === "english").length > 0
    );
  };

  const swedishIsReadyToUse = () => {
    return (
      linkOptions.translations &&
      linkOptions.translations.filter((t) => t === "swedish").length > 0
    );
  };

  return (
    <div>
      <div className="bg-gray-1 border border-gray-3 border-dashed rounded-3xl grow-0 shrink-0 xl:sticky xl:top-0 w-80 overflow-y-scroll">
        <div className="p-4 flex flex-col gap-1">
          <div className="mb-4">
            <DefaultLeadActions linkId={linkId} />
          </div>

          {showTemplateEditor && (
            <TemplateLinkSettings
              disabled={disabled}
              linkConfig={linkConfig}
              onUpdate={onUpdate}
            />
          )}

          {!isDefaultLead && (
            <LanguageLinkSettings
              linkConfig={linkConfig}
              onUpdate={onUpdate}
              selectedLanguage={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
              isTranslating={
                bgJobData?.isRunning || translationStarted || isLinkLoading
              }
              englishIsReadyToUse={englishIsReadyToUse() ?? false}
              swedishIsReadyToUse={swedishIsReadyToUse() ?? false}
              onTranslationStart={() => {
                setCheckForBackgroundJob(true);
                setTranslationStarted(true);
                onTranslationStarted();
              }}
              onTranslationEnd={() => {
                setTranslationStarted(false);
                onTranslationEnded();
              }}
            />
          )}

          <ProfileSummaryLinkSettings
            disabled={disabled}
            linkConfig={linkConfig}
            onUpdate={onUpdate}
            onGenerateNew={() => {
              updateAssignmentPitch(
                linkConfig?.includeNameInAssignmentPitch ?? false,
              );
            }}
          />
          <ProfileImageLinkSettings
            disabled={disabled}
            linkConfig={linkConfig}
            onUpdate={onUpdate}
          />

          {linkConfig?.highlightedProjects &&
            linkConfig?.highlightedProjects.length > 0 && (
              <HighlightedProjectsLinkSettings
                disabled={disabled}
                linkConfig={linkConfig}
                onUpdate={onUpdate}
              />
            )}
          <SkillStackLinkSettings
            disabled={disabled}
            linkConfig={linkConfig}
            onUpdate={onUpdate}
          />

          <WorkExperienceLinkSettings
            disabled={disabled}
            linkConfig={linkConfig}
            onUpdate={onUpdate}
          />
          <ProjectsLinkSettings
            disabled={disabled}
            linkConfig={linkConfig}
            onUpdate={onUpdate}
          />
          <PDFSettingsLinkSettings
            disabled={disabled}
            linkConfig={linkConfig}
            onUpdate={onUpdate}
            linkId={linkId}
            organizationId={organizationId}
            onPosthogCapture={(event, properties) =>
              posthog.capture(event, properties)
            }
          />

          <LinkSettingsLinkSettings
            disabled={disabled}
            linkConfig={linkConfig}
            onUpdate={onUpdate}
          />
        </div>
      </div>
    </div>
  );
};

const SettingRow = ({
  children,
  position = "start",
}: PropsWithChildren<{ position?: "start" | "end" }>) => (
  <div
    className={cn(
      "flex flex-row gap-2 items-center",
      position === "start" ? "justify-start" : "justify-end",
    )}
  >
    {children}
  </div>
);

const SettingText = ({ children }: PropsWithChildren) => (
  <p className="text-sm">{children}</p>
);

const SettingTitle = ({ children }: PropsWithChildren) => (
  <BulletSectionTitle className="pt-4">{children}</BulletSectionTitle>
);

interface DropdownOptionRowProps {
  onClick: () => void;
  icon?: any;
  text: string;
  iconClassName?: string;
  selected: boolean;
  profilePhotoId?: string | null;
  profileName?: string;
}

const DropdownOptionRow = ({
  icon,
  text,
  onClick,
  selected,
}: DropdownOptionRowProps) => (
  <PopoverMenu.Item onClick={onClick}>
    {selected && (
      <FontAwesomeIcon
        width={14}
        height={14}
        icon={faCheck}
        className="text-red-3 mr-2"
      />
    )}
    <span className="text-sm text-black">{text}</span>
  </PopoverMenu.Item>
);
